"use client";

import styles from "./businessType.module.scss";
import { useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import ToggleSwitchV2 from "@/components/toggelSwictherV2/toggelSwictherV2";
import { svgImages } from "@/lib/svgImages";
import { useLanguage } from "@/contexts/LanguageContext";
import { ArrowLeft, ArrowRight, ShoppingBag } from "lucide-react";
import Image from "next/image";
import { DashboardCreateLink } from "@/components/dashboardCreateLink/dashboardCreateLink";

const BusinessType = () => {
  const { t, isRTL } = useLanguage();
  const [isDynamic, setIsDynamic] = useState(false);

  function FadeInSection({
    children,
    className = "",
    delay = 0,
  }: {
    children: React.ReactNode;
    className?: string;
    delay?: number;
  }) {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: "-80px" });
    return (
      <motion.div
        ref={ref}
        initial={{ opacity: 0, y: 40 }}
        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
        transition={{ duration: 0.6, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
        className={className}
        style={{
          willChange: "transform, opacity",
          WebkitBackfaceVisibility: "hidden",
          backfaceVisibility: "hidden",
        }}
      >
        {children}
      </motion.div>
    );
  }

  return (
    <div className={`${styles["productsTabsSectionV2"]} mt-24`}>
      <div className="custom-container">
        <div className={`${styles["productsTabsSectionDesktop"]}`}>
          <div className="mb-10">
            <h2>
              <span
                className="text-4xl sm:text-5xl lg:text-6xl font-bold"
                style={{
                  fontFamily: '"Inter", ui-sans-serif, system-ui, sans-serif',
                }}
              >
                {t("businessType.titleFirst")}{" "}
                <span className="text-meno-orange">
                  {t("businessType.titleThird")}{" "}
                </span>
                {t("businessType.titleSecond")}{" "}
                <span className="text-[#bfa690]">
                  {t("businessType.titleFourth")}
                </span>
              </span>
            </h2>
            <div
              className="text-slate-500 mt-3"
              style={{
                fontFamily: '"Inter", ui-sans-serif, system-ui, sans-serif',
              }}
            >
              {t("businessType.sub")}
            </div>
          </div>
          <div>
            <ToggleSwitchV2
              menoPage={true}
              isRTL={isRTL}
              isChecked={isDynamic}
              setIsChecked={setIsDynamic}
              firstVal={t("businessType.toggleProducts")}
              firstIcon={
                <span
                  dangerouslySetInnerHTML={{
                    __html: svgImages.arrowRight,
                  }}
                />
              }
              secondVal={t("businessType.toggleFood")}
              secondIcon={
                <span
                  dangerouslySetInnerHTML={{
                    __html: svgImages.arrowRight,
                  }}
                />
              }
              pricingPage={false}
            />
          </div>
          {!isDynamic ? (
            <div>
              <div className="overflow-hidden">
                <section
                  className={`${styles.heroGradientFood} rounded-3xl relative mt-10 border-none overflow-hidden`}
                >
                  <div
                    className={`${styles.heroCardInner} relative z-10 flex flex-col lg:flex-row items-center`}
                  >
                    <FadeInSection className="lg:w-1/2 pb-4 p-8 sm:p-12 lg:p-20 text-start">
                      <div className="inline-flex items-center gap-2 bg-[#bbdfff] text-black text-sm font-bold px-4 py-1.5 rounded-full mb-6">
                        <ShoppingBag
                          aria-hidden="true"
                          focusable="false"
                          className="w-4 h-4"
                        />
                        {t("businessType.food.badge")}
                      </div>

                      <h2 className="font-extrabold text-gray-900">
                        <span
                          className="text-3xl md:text-4xl lg:text-6xl text-gray-900"
                          style={{
                            fontFamily:
                              '"Inter", ui-sans-serif, system-ui, sans-serif',
                          }}
                        >
                          {t("businessType.food.title1")}
                          <span
                            className={`block text-2xl md:text-3xl lg:text-4xl font-semibold text-white `}
                          >
                            {t("businessType.food.title2")}
                          </span>
                        </span>
                      </h2>

                      <div className="text-orange-50 text-lg leading-relaxed mt-4 mb-6 max-w-xl">
                        {t("businessType.food.desc")}
                      </div>

                      <div className="flex flex-wrap gap-3 mb-8">
                        {(["tag1", "tag2", "tag3", "tag4"] as const).map(
                          (key, i) => (
                            <div
                              key={i}
                              className="bg-white/15 backdrop-blur-sm border border-white/55 px-4 py-2 rounded-full flex items-center gap-2 shadow-sm"
                            >
                              <div className="w-2 h-2 rounded-full bg-white" />
                              <span className="text-sm font-bold text-white">
                                {t(`businessType.food.${key}`)}
                              </span>
                            </div>
                          ),
                        )}
                      </div>

                      <ul className="text-orange-50 text-sm mt-4 p-4">
                        <li>{t("businessType.food.sub1")}</li>
                        <li>{t("businessType.food.sub2")}</li>
                        <li>{t("businessType.food.sub3")}</li>
                      </ul>

                      <DashboardCreateLink
                        meno
                        className="inline-flex items-center justify-center gap-2 bg-black text-white font-bold text-base px-8 py-4 rounded-full transition-all hover:scale-105 active:scale-95"
                        target="_blank"
                      >
                        {t("hero.cta")}
                        {isRTL ? (
                          <ArrowLeft aria-hidden="true" className="w-5 h-5" />
                        ) : (
                          <ArrowRight aria-hidden="true" className="w-5 h-5" />
                        )}
                      </DashboardCreateLink>
                    </FadeInSection>

                    <FadeInSection
                      className={`${styles.heroImageColumn}`}
                      delay={0.2}
                    >
                      <div className={styles.heroImageFrame}>
                        <Image
                          src="/images/business-type/meno-restaurants-desktop.webp"
                          alt="Product catalogs on phone screens"
                          className={`${styles.heroImage} ${styles.heroImageFood}`}
                          width={1000}
                          height={700}
                          sizes="(max-width: 992px) 100vw, 50vw"
                          priority
                        />
                      </div>
                    </FadeInSection>
                  </div>
                </section>
              </div>
            </div>
          ) : (
            <div>
              <div className="overflow-hidden">
                <section
                  className={`${styles.heroGradientRetail} rounded-3xl overflow-hidden relative mt-10`}
                >
                  <div
                    className={`${styles.heroCardInner} relative z-10 flex flex-col lg:flex-row items-center`}
                  >
                    <FadeInSection className="lg:w-1/2 pb-4 p-8 sm:p-12 lg:p-20 text-start">
                      <div
                        className={`inline-flex items-center gap-2 bg-meno-cyan text-black text-sm font-bold px-4 py-1.5 rounded-full mb-6`}
                      >
                        <ShoppingBag
                          aria-hidden="true"
                          focusable="false"
                          className="w-4 h-4"
                        />
                        {t("products.badge")}
                      </div>

                      <h2 className="font-extrabold text-gray-900">
                        <span
                          className="text-3xl md:text-4xl lg:text-6xl text-gray-900"
                          style={{
                            fontFamily:
                              '"Inter", ui-sans-serif, system-ui, sans-serif',
                          }}
                        >
                          {t("products.title1")}
                          <span
                            className={`block text-2xl md:text-3xl lg:text-4xl font-semibold text-white ${isRTL ? "pt-6" : ""}`}
                          >
                            {t("products.title2")}
                          </span>
                        </span>
                      </h2>

                      <div className="text-white text-lg leading-relaxed mt-4 mb-6 max-w-xl">
                        {t("products.desc")}
                      </div>

                      <div className={`flex flex-wrap gap-3 mb-8`}>
                        {(["tag1", "tag2", "tag3"] as const).map((key, i) => (
                          <div
                            key={i}
                            className="bg-white/15 backdrop-blur-sm border border-white/55 px-4 py-2 rounded-full flex items-center gap-2 shadow-sm"
                          >
                            <div className="w-2 h-2 rounded-full bg-white" />
                            <span className="text-sm font-bold text-white">
                              {t(`products.${key}`)}
                            </span>
                          </div>
                        ))}
                      </div>

                      <ul className="text-orange-50 text-sm mt-4 p-4">
                        <li>{t("products.sub1")}</li>
                        <li>{t("products.sub2")}</li>
                        <li>{t("products.sub3")}</li>
                      </ul>

                      <DashboardCreateLink
                        meno
                        className="inline-flex items-center justify-center gap-2 bg-black text-white font-bold text-base px-8 py-4 rounded-full transition-all hover:scale-105 active:scale-95 "
                        target="_blank"
                      >
                        {t("hero.cta")}

                        {isRTL ? (
                          <ArrowLeft aria-hidden="true" className="w-5 h-5" />
                        ) : (
                          <ArrowRight aria-hidden="true" className="w-5 h-5" />
                        )}
                      </DashboardCreateLink>
                    </FadeInSection>

                    <FadeInSection
                      className={`${styles.heroImageColumn}`}
                      delay={0.2}
                    >
                      <div className={styles.heroImageFrame}>
                        <Image
                          src="/images/business-type/meno-retail-desktop.webp"
                          alt="Product catalogs on phone screens"
                          className={`${styles.heroImage} ${styles.heroImageRetail}`}
                          width={1000}
                          height={600}
                          sizes="(max-width: 992px) 100vw, 50vw"
                          priority
                        />
                      </div>
                    </FadeInSection>
                  </div>
                </section>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BusinessType;
